// Step-by-step instructions per source (modal on click, direct link `#<source>`).
// Pure data and helpers - no DOM; the texts live in i18n/en.ts under `guide.<id>.*`.
import { t, type MessageKey } from "./i18n";
import type { ConnProvider } from "./lib";

export const GUIDE_IDS = [
  "ga4",
  "google_ads",
  "search_console",
  "meta",
  "fb_pages",
  "instagram",
  "tiktok",
  "apple_ads",
  "appsflyer",
  "revenuecat",
  "app_store_connect",
  "google_play",
  "apple",
  "plugin",
  "chatgpt",
] as const;

export type GuideId = (typeof GUIDE_IDS)[number];

export interface GuideDef {
  /** Provider whose “Authorize” connects the source; none for key-based and public sources. */
  provider?: ConnProvider;
  /** The extension settings field the key goes into. */
  keyField?: string;
  /** The anchor in links, when it differs from the id (`#app-store-connect`). */
  anchor?: string;
  /** Where the key is entered: the action button leads to the Connections page. */
  connections?: boolean;
  /** Not a source: an instruction that lives on this page (its modal and direct link). */
  page?: string;
}

export const GUIDES: Record<GuideId, GuideDef> = {
  ga4: { provider: "google" },
  google_ads: { provider: "google" },
  search_console: { provider: "google" },
  meta: { provider: "meta" },
  fb_pages: { provider: "meta" },
  instagram: { provider: "meta" },
  tiktok: { provider: "tiktok" },
  apple_ads: { provider: "apple_ads", anchor: "apple-ads" },
  appsflyer: { keyField: "AppsFlyer token" },
  revenuecat: { keyField: "RevenueCat secret API key" },
  app_store_connect: { anchor: "app-store-connect", connections: true },
  google_play: { provider: "google_play", anchor: "google-play" },
  apple: {},
  plugin: { page: "/install" },
  chatgpt: { page: "/install" },
};

export function isGuideId(v: unknown): v is GuideId {
  return typeof v === "string" && (GUIDE_IDS as readonly string[]).includes(v);
}

/** `#appsflyer` → "appsflyer"; anything else → null. */
export function guideFromHash(hash: string | null | undefined): GuideId | null {
  const id = decodeURIComponent((hash ?? "").replace(/^#/, ""));
  if (isGuideId(id)) return id;
  return GUIDE_IDS.find((g) => GUIDES[g].anchor === id) ?? null;
}

/** The URL anchor of a guide: `app_store_connect` → "app-store-connect". */
export function guideAnchor(id: GuideId): string {
  return GUIDES[id].anchor ?? id;
}

/** Landing "Sources": the label shown, the guide it opens and the group it sits in. */
export type SourceGroup = "paid" | "organic" | "product" | "stores";
export const SOURCE_GROUPS: SourceGroup[] = ["paid", "organic", "product", "stores"];
export const LANDING_SOURCES: { label: string; id: GuideId; group: SourceGroup }[] = [
  { label: "Meta Ads", id: "meta", group: "paid" },
  { label: "Google Ads", id: "google_ads", group: "paid" },
  { label: "TikTok Ads", id: "tiktok", group: "paid" },
  { label: "Apple Ads", id: "apple_ads", group: "paid" },
  { label: "Facebook Pages", id: "fb_pages", group: "organic" },
  { label: "Instagram", id: "instagram", group: "organic" },
  { label: "Search Console", id: "search_console", group: "organic" },
  { label: "GA4", id: "ga4", group: "product" },
  { label: "AppsFlyer", id: "appsflyer", group: "product" },
  { label: "RevenueCat", id: "revenuecat", group: "product" },
  { label: "App Store Connect", id: "app_store_connect", group: "stores" },
  { label: "Google Play", id: "google_play", group: "stores" },
  { label: "App Store · public", id: "apple", group: "stores" },
];

const key = (id: GuideId, part: "title" | "intro" | "needs" | "steps" | "errors") => `guide.${id}.${part}` as MessageKey;

/** A multi-line text entry → its non-empty lines. */
export function lines(text: string): string[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}

export interface ErrorLine {
  problem: string;
  fix: string;
}

/** "symptom → what to do"; a line without an arrow is all fix. */
export function splitError(line: string): ErrorLine {
  const i = line.indexOf(" → ");
  return i < 0 ? { problem: "", fix: line } : { problem: line.slice(0, i), fix: line.slice(i + 3) };
}

export interface GuideText {
  title: string;
  intro: string;
  needs: string[];
  steps: string[];
  errors: ErrorLine[];
  common: ErrorLine[];
}

export function guideText(id: GuideId): GuideText {
  return {
    title: t(key(id, "title")),
    intro: t(key(id, "intro")),
    needs: lines(t(key(id, "needs"))),
    steps: lines(t(key(id, "steps"))),
    errors: lines(t(key(id, "errors"))).map(splitError),
    common: GUIDES[id].page ? [] : lines(t("guide.common.errors")).map(splitError),
  };
}

export type TextPart = { text: string } | { href: string };

/** Splits plain text into text and https:// links (rendered as <a>). */
export function linkify(text: string): TextPart[] {
  const out: TextPart[] = [];
  const re = /https:\/\/[^\s)]+[^\s).,;]/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index! > last) out.push({ text: text.slice(last, m.index) });
    out.push({ href: m[0] });
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/** Elements that can take focus inside the dialog, in tab order. */
export const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Index of the element Tab should move to (wraps inside the dialog). */
export function nextFocusIndex(current: number, count: number, back: boolean): number {
  if (count <= 0) return -1;
  if (current < 0) return back ? count - 1 : 0;
  return back ? (current - 1 + count) % count : (current + 1) % count;
}
